// src/components/ai/AISuggestionPanel.js
// Enhanced AI Technician Assignment Suggestion Panel with modern UI/UX

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Avatar,
  Badge,
  Divider,
  useColorModeValue,
  useToast,
  Flex,
  Icon,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  Tag,
  TagLabel,
  TagLeftIcon,
  Collapse,
  List,
  ListItem,
  ListIcon,
  ScaleFade,
  Skeleton,
  SkeletonCircle,
  SkeletonText
} from '@chakra-ui/react';
import {
  MdPerson,
  MdCheckCircle,
  MdInfo,
  MdLocationOn,
  MdAccessTime,
  MdBuild,
  MdStar,
  MdTrendingUp,
  MdExpandMore,
  MdExpandLess
} from 'react-icons/md';
import * as workOrderService from '../../services/workOrderService';

/**
 * AISuggestionPanel Component
 * Displays AI-recommended technician for work order assignment
 * with detailed reasoning and modern UI
 */
const AISuggestionPanel = ({ suggestion, notes, workOrderId, onAssignmentSuccess }) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  const [isAccepting, setIsAccepting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const toast = useToast();

  // ============================================================================
  // THEME COLORS (LIGHT & DARK MODE SUPPORT)
  // ============================================================================
  const bgGradient = useColorModeValue(
    'linear(to-r, blue.50, purple.50)',
    'linear(to-r, blue.900, purple.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.600', 'blue.300');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const iconColor = useColorModeValue('blue.500', 'blue.200');
  const collapseBg = useColorModeValue('gray.50', 'gray.700');

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles accepting the AI suggestion and assigning the technician
   */
  const handleAcceptSuggestion = async () => {
    if (!workOrderId || !suggestion) {
      toast({
        title: 'Error',
        description: 'Missing work order or suggestion data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsAccepting(true);
    try {
      await workOrderService.acceptSuggestion(workOrderId);

      toast({
        title: 'Assignment Successful',
        description: `${suggestion.name} has been assigned to this work order.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Notify parent component to refresh
      if (onAssignmentSuccess) {
        onAssignmentSuccess();
      }

    } catch (error) {
      console.error('[AISuggestionPanel] Error accepting suggestion:', error);
      toast({
        title: 'Assignment Failed',
        description: error.message || 'Could not assign the suggested technician.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAccepting(false);
    }
  };

  /**
   * Parses the AI notes to extract key points
   */
  const parseNotes = (notesText) => {
    if (!notesText) return [];
    
    // Split by common delimiters and filter empty lines
    const points = notesText
      .split(/[.\n]/)
      .map(p => p.trim())
      .filter(p => p.length > 10); // Filter out very short fragments

    return points.slice(0, 5); // Limit to top 5 points
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // If no suggestion, show info message
  if (!suggestion) {
    return (
      <Alert
        status="info"
        variant="left-accent"
        borderRadius="lg"
        boxShadow="sm"
      >
        <AlertIcon as={MdInfo} />
        <Box flex={1}>
          <AlertTitle fontSize="md">No AI Suggestion Available</AlertTitle>
          <AlertDescription fontSize="sm">
            Our AI is analyzing this work order. A technician recommendation will appear here shortly.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  const notePoints = parseNotes(notes);

  return (
    <ScaleFade in={true} initialScale={0.95}>
      <Box
        bgGradient={bgGradient}
        borderRadius="xl"
        p={1}
        boxShadow="lg"
      >
        <Box
          bg={cardBg}
          borderRadius="lg"
          p={5}
        >
          {/* Header */}
          <Flex align="center" justify="space-between" mb={4}>
            <HStack spacing={3}>
              <Flex
                bg={iconBg}
                p={3}
                borderRadius="lg"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={MdStar} color={iconColor} boxSize={6} />
              </Flex>
              <Box>
                <Heading size="md" color={headingColor}>
                  AI Recommendation
                </Heading>
                <Text fontSize="sm" color={mutedTextColor}>
                  Powered by FlowSense AI
                </Text>
              </Box>
            </HStack>
            <Tag size="lg" colorScheme="purple" variant="subtle" borderRadius="full">
              <TagLeftIcon as={MdTrendingUp} />
              <TagLabel>Best Match</TagLabel>
            </Tag>
          </Flex>

          <Divider my={4} />

          {/* Suggested Technician */}
          <HStack spacing={4} mb={4}>
            <Avatar
              size="lg"
              name={suggestion.name}
              bg={accentColor}
              color="white"
              icon={<Icon as={MdPerson} boxSize={8} />}
            />
            <VStack align="flex-start" spacing={1} flex={1}>
              <HStack>
                <Text fontSize="xl" fontWeight="bold" color={headingColor}>
                  {suggestion.name}
                </Text>
                <Badge colorScheme="green" fontSize="xs">
                  Available
                </Badge>
              </HStack>
              {suggestion.email && (
                <Text fontSize="sm" color={textColor}>
                  {suggestion.email}
                </Text>
              )}
              <HStack spacing={3} mt={1}>
                <Tooltip label="Expert in this area">
                  <HStack spacing={1}>
                    <Icon as={MdBuild} color={iconColor} boxSize={4} />
                    <Text fontSize="xs" color={mutedTextColor}>
                      Specialist
                    </Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Nearby location">
                  <HStack spacing={1}>
                    <Icon as={MdLocationOn} color={iconColor} boxSize={4} />
                    <Text fontSize="xs" color={mutedTextColor}>
                      Close by
                    </Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Fast response time">
                  <HStack spacing={1}>
                    <Icon as={MdAccessTime} color={iconColor} boxSize={4} />
                    <Text fontSize="xs" color={mutedTextColor}>
                      Quick
                    </Text>
                  </HStack>
                </Tooltip>
              </HStack>
            </VStack>
          </HStack>

          {/* AI Reasoning - Collapsible */}
          {notePoints.length > 0 && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDetails(!showDetails)}
                rightIcon={<Icon as={showDetails ? MdExpandLess : MdExpandMore} />}
                width="full"
                justifyContent="space-between"
                mb={2}
              >
                <Text fontSize="sm" fontWeight="medium">
                  Why this recommendation?
                </Text>
              </Button>

              <Collapse in={showDetails} animateOpacity>
                <Box
                  bg={collapseBg}
                  borderRadius="md"
                  p={4}
                  mb={4}
                >
                  <Text fontSize="xs" fontWeight="bold" color={mutedTextColor} mb={2}>
                    AI ANALYSIS
                  </Text>
                  <List spacing={2}>
                    {notePoints.map((point, index) => (
                      <ListItem key={index} fontSize="sm" color={textColor}>
                        <HStack align="flex-start">
                          <ListIcon as={MdCheckCircle} color={iconColor} mt={0.5} />
                          <Text>{point}</Text>
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Collapse>
            </>
          )}

          {/* Action Buttons */}
          <HStack spacing={3} mt={4}>
            <Button
              colorScheme="blue"
              size="md"
              flex={1}
              onClick={handleAcceptSuggestion}
              isLoading={isAccepting}
              loadingText="Assigning..."
              leftIcon={<Icon as={MdCheckCircle} />}
            >
              Accept & Assign
            </Button>
            <Tooltip label="Manual assignment available in the toolbar">
              <Button
                variant="outline"
                size="md"
                isDisabled={isAccepting}
              >
                Choose Different
              </Button>
            </Tooltip>
          </HStack>

          {/* Footer Note */}
          <Text fontSize="xs" color={mutedTextColor} textAlign="center" mt={4}>
            This suggestion is based on workload, expertise, location, and historical performance.
          </Text>
        </Box>
      </Box>
    </ScaleFade>
  );
};

// ============================================================================
// LOADING SKELETON
// ============================================================================

/**
 * Skeleton loader for when suggestion data is loading
 */
export const AISuggestionPanelSkeleton = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      p={5}
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="md"
    >
      <HStack spacing={4} mb={4}>
        <SkeletonCircle size="16" />
        <VStack align="flex-start" spacing={2} flex={1}>
          <Skeleton height="20px" width="200px" />
          <Skeleton height="16px" width="150px" />
        </VStack>
      </HStack>
      <SkeletonText mt={4} noOfLines={3} spacing={4} />
      <HStack spacing={3} mt={6}>
        <Skeleton height="40px" flex={1} borderRadius="md" />
        <Skeleton height="40px" width="120px" borderRadius="md" />
      </HStack>
    </Box>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

AISuggestionPanel.propTypes = {
  suggestion: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    email: PropTypes.string,
  }),
  notes: PropTypes.string,
  workOrderId: PropTypes.string.isRequired,
  onAssignmentSuccess: PropTypes.func,
};

export default AISuggestionPanel;